import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Collapse, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { selectMultiStationData } from '@awp/stores/dataVisualizationSlice';
import GraphLegend from '@components/Graphs/MultiStation/Legend';
import Graph from '@components/Graphs/Common/Graph';
import { selectAllWeatherStations } from '@stores/weatherStationSlice';
import { useMultiStationContext } from '@components/Graphs/MultiStation/MultiStationContext';
import { IconTableColumns } from '@awp/components/awpIcons';
import Color from 'color';
import { DATASET_TYPES } from '@awp/constants';
import { isWxnetDataset, isSelectedWxnetDataset } from '@awp/helpers/utilsReact';
/**
 * React component for rendering a graph container with optional legend and toggles.
 * This component integrates various configurations and displays them based on user interaction and data availability.
 *
 * @param {Object} props - The props object containing configuration for the graph.
 * @param {Object} props.dataGraph - Object containing specific settings and data references for the graph.
 * @returns {JSX.Element} The JSX element representing the graph container, possibly including a legend.
 */
const GraphContainer = (props) => {
  const { dataGraph } = props;
  // Context and selector hooks to fetch state from Redux and context.
  const { weatherStations, weatherStationId, showLegend, heatMapObservation, forecastWeatherStationId } =
    useMultiStationContext();

  const allWeatherStations = useSelector(selectAllWeatherStations);
  const data = useSelector(selectMultiStationData);

  // Local state for handling UI toggles and selections
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showLegendLocal, setShowLegendLocal] = useState(showLegend);

  // Calculate dynamic widths for responsive layout based on legend visibility
  const graphWidth = showLegendLocal ? 10 : 12;
  const legendWidth = showLegendLocal ? 2 : 0;

  /**
   * Toggles the visibility of the local legend state.
   */
  const toggleLegendLocal = () => {
    setShowLegendLocal(!showLegendLocal);
  };

  useEffect(() => {
    // Synchronize local legend visibility with the context state.
    setShowLegendLocal(showLegend);
  }, [showLegend]);

  useEffect(() => {
    // Early exit if data is not yet available to prevent errors.
    if (!data) return;
    let heatMapPreset = null;
    /**
     * Processes graph filters to manage how data is displayed or deferred in the UI.
     * Includes managing deferred weather stations and merging with current configuration.
     */
    const graphFilters = dataGraph.PersistentGraphFilters.filter((pgf) => pgf.WeatherStationId === null)
      .map((p) => {
        const deferredWeatherStation =
          weatherStationId !== -1 ? allWeatherStations.find((wx) => wx.Id === weatherStationId) : null;
        const deferredWeatherStationInfo = deferredWeatherStation
          ? {
              DropdownText: deferredWeatherStation.DropdownText,
              Id: deferredWeatherStation.Id,
              Name: deferredWeatherStation.Name,
              Number: deferredWeatherStation.Number,
            }
          : null;

        return {
          ...p,
          WeatherStationId: weatherStationId !== -1 ? weatherStationId : null,
          weatherStationInfo: deferredWeatherStationInfo,
        };
      })
      .concat(dataGraph.PersistentGraphFilters.filter((pgf) => pgf.WeatherStationId !== null));
    /**
     * Constructs the datasets for the graph by filtering and mapping the necessary data
     * points from the available data based on the active graph filters.
     */
    const results = graphFilters
      .filter((pgf) => pgf.WeatherStationId !== null)
      .map((ds) => {
        const dataDTO = data.DataVisualizationDTOs.find((d) => d.WeatherStationId === ds.WeatherStationId);
        if (ds.ObservationTypeId === heatMapObservation.ObservationTypeId) {
          heatMapPreset = ds;
        }
        const obs = dataDTO?.Datasets.filter((s) => {
          //heat map & snow pole observations will come out in graph setup
          // if (ds.ObservationTypeId === heatMapObservation.ObservationTypeId) {
          //   return (
          //     s.type === DATASET_TYPES.FrostProbe &&
          //     s.graphConfig?.observationTypeName === FROST_PROBE_CONFIG.SUBSFC_TEMP
          //   );
          // }

          // Check if the PlacementName is null and check if it is the selected station if dataset(s) are wxnet
          if (ds.PlacementName === null) {
            return (
              s.observationTypeId === ds.ObservationTypeId &&
              (!isWxnetDataset(s) || isSelectedWxnetDataset(s, ds, forecastWeatherStationId))
            );
          }
          // Check if the PlacementName is equals 'Camera Image'
          if (ds.PlacementName === DATASET_TYPES.CameraImage) {
            return s.observationTypeId === ds.ObservationTypeId;
          }
          // Check if the PlacementName matches and has the same observationTypeId
          //If it is weatherNet pavement, check if the dataset belongs to the selected station in forecast panel
          //below scenarios:
          //regular dataset(s) || wxnet pavement dataset(s) (non-deferred)
          if (
            s.PlacementName === ds.PlacementName &&
            (!isWxnetDataset(s) || isSelectedWxnetDataset(s, ds, forecastWeatherStationId))
          ) {
            return s.observationTypeId === ds.ObservationTypeId;
          }

          // Default case to handle unexpected scenarios (this can be omitted if unnecessary)
          return false;
        });

        return (
          obs?.map((o) => ({
            ...o,
            graphConfig: {
              ...o.graphConfig,
              graphColor: ds.ColorCodeOverride || o.graphConfig.graphColor,
              colorOverridden: !!ds.ColorCodeOverride,
            },
            weatherStation: ds.WeatherStationId,
            weatherStationText: ds.weatherStationInfo.DropdownText,
          })) || []
        );
      })
      .flat()
      .filter((e) => e !== undefined);
    /**
     * Formats the data options for the graph, managing colors and grouping settings.
     */
    // const options = results.map((ds) => ({
    //   group: ds.group,
    //   typeId: ds.observationTypeId,
    //   value: ds.DatasetId,
    //   label: ds.label,
    //   color: ds.graphConfig.graphColor || '#000',
    //   colorOverridden: ds.graphConfig.colorOverridden,
    //   weatherStationId: ds.weatherStation,
    //   weatherStationText: ds.weatherStationText,
    // }));
    const options = results
      // .filter(
      //   (ds) =>
      //     !(
      //       ds.type === DATASET_TYPES.FrostProbe &&
      //       ds.graphConfig?.observationTypeName === FROST_PROBE_CONFIG.SUBSFC_TEMP
      //     ),
      // )
      .map((ds) => ({
        group: ds.group,
        typeId: ds.observationTypeId,
        value: ds.DatasetId,
        label: ds.label,
        color: ds.graphConfig.graphColor || '#000',
        colorOverridden: ds.graphConfig.colorOverridden,
        weatherStationId: ds.weatherStation,
        weatherStationText: ds.weatherStationText,
      }));
    //TODO: need to pull color data & weather station from pgf to set color
    if (heatMapPreset && heatMapPreset?.WeatherStationId > 0) {
      options.push({
        group: DATASET_TYPES.FrostProbe,
        typeId: heatMapObservation.ObservationTypeId,
        value: heatMapObservation.ObservationTypeId,
        label: heatMapObservation.ObservationTypeDisplayName,
        color: heatMapPreset.ColorCodeOverride || '#000',
        colorOverridden: !!heatMapPreset.ColorCodeOverride,
        weatherStationId: heatMapPreset.WeatherStationId,
        weatherStationText: heatMapPreset.weatherStationInfo.DropdownText,
      });
    }

    //sort by labels for color adjustment
    //options.sort((a, b) => a.label.localeCompare(b.label));
    options.sort((a, b) => {
      // First compare the labels
      const labelComparison = a.label.localeCompare(b.label);
      if (labelComparison !== 0) {
        return labelComparison;
      }
      // If labels are the same, compare the groups
      return a.group.localeCompare(b.group);
    });

    // Adjust colors for readability and visual distinction.
    const groupedObjects = options.reduce((acc, obj) => {
      const key = obj.typeId + obj.color;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
    let adjustedColors = Object.values(groupedObjects).flatMap((objs) => {
      let { h, s, l } = Color(objs[0].color).hsl().object();
      const origL = l;
      let tintAdj = Math.round((85 - l) / objs.length);

      return objs.map((obj) => {
        if (Math.min(l, 90) > 90) {
          l = origL;
        }
        obj.color = Color({ h, s, l }).hex().toLowerCase();
        l += tintAdj;
        return obj;
      });
    });
    //check to remove any duplicated observations
    // caused by deferred setting same observations that may already be preset (WEAV-14091)
    adjustedColors = adjustedColors.filter(
      (
        (seen) => (item) =>
          !seen.has(item.value + item.weatherStationId) && seen.add(item.value + item.weatherStationId)
      )(new Set()),
    );
    //re-apply the sort after color adjustments
    adjustedColors.sort((a, b) => {
      // First compare the labels
      const labelComparison = a.label.localeCompare(b.label);
      if (labelComparison !== 0) {
        return labelComparison;
      }
      // If labels are the same, compare the groups
      return a.group.localeCompare(b.group);
    });
    setSelectedOptions(adjustedColors);
  }, [dataGraph, data, weatherStationId, forecastWeatherStationId]);
  return (
    <>
      {selectedOptions.length > 0 && data?.DataVisualizationDTOs ? (
        <Card border="light" className={'page-card__fullscreen'}>
          <Card.Header>
            {dataGraph?.Name}
            <OverlayTrigger
              overlay={
                <Tooltip id={`tooltip-${dataGraph.DataGraphId}`}>
                  {showLegendLocal ? 'Hide Legend' : 'Show Legend'}
                </Tooltip>
              }
            >
              <IconTableColumns
                alt="Toggle Legend"
                title="Toggle Legend"
                style={{
                  width: '2rem',
                  height: '2rem',
                  paddingLeft: '.5rem',
                  cursor: 'pointer',
                  float: 'right',
                }}
                onClick={toggleLegendLocal}
              />
            </OverlayTrigger>
          </Card.Header>
          <Col lg={1} md={1} className={'p-0'}></Col>
          <Card.Body className={'pt-0 pb-0'}>
            <div className={`graph__container__fullscreen ${showLegendLocal ? 'show-legend' : ''}`}>
              <Row>
                <Col lg={graphWidth} md={graphWidth} className="container-transition graph-transition">
                  <Graph dataGraph={dataGraph} selectedOptions={selectedOptions} />
                </Col>
                <Collapse in={showLegendLocal}>
                  <Col
                    lg={legendWidth}
                    md={legendWidth}
                    className="container-transition legend-transition multiwx-legend"
                  >
                    <div id={`${dataGraph.DataGraphId}_Legend`} className="legend__container">
                      <GraphLegend
                        selectedOptions={selectedOptions}
                        dataGraphId={dataGraph.DataGraphId}
                        weatherStations={weatherStations}
                      />
                    </div>
                  </Col>
                </Collapse>
              </Row>
            </div>
          </Card.Body>
        </Card>
      ) : null}
    </>
  );
};

export default GraphContainer;
